import { NetworkStatus } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { Button, Card, Flex, SimpleGrid, Spinner, Text } from '@chakra-ui/react'
import { Link } from 'react-router-dom'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { AdminBadge } from '../../../components/StatusBadge/StatusBadge'
import { showToast } from '../../../components/Toast/showToast'
import {
  CustomerDataApiStatus,
  clearCustomerDataApiRuntimeCacheMutation,
  customerDataApiDeploymentsQuery,
  customerDataApiStatusQuery,
  reloadCustomerDataApiRuntimeMutation,
} from '../../../services/customerDataApiService'
import { formatAdminDateTime } from '../../../utils/dates'
import {
  ControlsCard,
  DeploymentCard,
  LiveRuntimeCard,
  ServiceCard,
  StatusCallout,
  SubgraphRuntimeCard,
  defaultDeploymentsListVariables,
} from './DBSubgraphsAdminPage.parts'

export const DBSubgraphsAdminPage = () => {
  const {
    data: statusData,
    loading: isLoading,
    networkStatus,
    error,
  } = useQuery(customerDataApiStatusQuery, {
    fetchPolicy: 'network-only',
    pollInterval: 10000,
    notifyOnNetworkStatusChange: true,
  })

  const status = (
    statusData as { customerDataApiStatus?: CustomerDataApiStatus } | undefined
  )?.customerDataApiStatus
  const isFetching =
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.poll

  const [reloadMutation, { loading: reloadMutationLoading }] = useMutation(
    reloadCustomerDataApiRuntimeMutation,
    {
      refetchQueries: [
        { query: customerDataApiStatusQuery },
        {
          query: customerDataApiDeploymentsQuery,
          variables: defaultDeploymentsListVariables,
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: result => {
        const r = (
          result as {
            reloadCustomerDataApiRuntime?: { success: boolean; message: string }
          }
        )?.reloadCustomerDataApiRuntime
        showToast({
          title: r?.success ? 'Reload started' : 'Reload failed',
          description: r?.message ?? '',
          variant: r?.success ? 'success' : 'error',
        })
      },
      onError: mutationError => {
        showToast({
          title: 'Reload failed',
          description:
            mutationError instanceof Error
              ? mutationError.message
              : 'Unable to reload DB subgraphs',
          variant: 'error',
        })
      },
    },
  )

  const [clearCacheMutation, { loading: clearCacheMutationLoading }] =
    useMutation(clearCustomerDataApiRuntimeCacheMutation, {
      refetchQueries: [{ query: customerDataApiStatusQuery }],
      awaitRefetchQueries: true,
      onCompleted: result => {
        const r = (
          result as {
            clearCustomerDataApiRuntimeCache?: {
              success: boolean
              message: string
            }
          }
        )?.clearCustomerDataApiRuntimeCache
        showToast({
          title: r?.success ? 'Cache cleared' : 'Cache clear failed',
          description: r?.message ?? '',
          variant: r?.success ? 'success' : 'error',
        })
      },
      onError: mutationError => {
        showToast({
          title: 'Cache clear failed',
          description:
            mutationError instanceof Error
              ? mutationError.message
              : 'Unable to clear cache',
          variant: 'error',
        })
      },
    })

  if (isLoading && !status) {
    return (
      <AdminPageLayout pageTitle="Customer Data API Status">
        <Flex align="center" gap="12px">
          <Spinner size="sm" />
          <Text>Loading Customer Data API status...</Text>
        </Flex>
      </AdminPageLayout>
    )
  }

  if (error && !status) {
    return (
      <AdminPageLayout pageTitle="Customer Data API Status">
        <StatusCallout
          title="Status unavailable"
          tone="red"
          message={
            error instanceof Error
              ? error.message
              : 'Unable to load Customer Data API status.'
          }
        />
      </AdminPageLayout>
    )
  }

  if (!status) {
    return (
      <AdminPageLayout pageTitle="Customer Data API Status">
        <StatusCallout
          title="Status unavailable"
          tone="red"
          message="Unable to load Customer Data API status."
        />
      </AdminPageLayout>
    )
  }

  const runtime = status.managerRuntime
  const subgraphs = runtime?.subgraphs ?? []
  const deploymentInProgress = status.inProgressDeployments.length > 0
  const controlsDisabled = !status.manager.accessible

  return (
    <AdminPageLayout pageTitle="Customer Data API Status">
      <Flex direction="column" gap="24px" minHeight="100vh">
        <Flex justify="space-between" align="start" gap="16px" wrap="wrap">
          <Flex direction="column" gap="4px">
            <Text variant="sectionHeader">Customer Data API Runtime</Text>
            <Text fontSize="sm" color="presets.neutral.70">
              Last checked {formatAdminDateTime(status.checkedAt)}
              {isFetching ? ' · refreshing...' : ''}
            </Text>
          </Flex>
          <ControlsCard
            disabled={controlsDisabled}
            reloading={reloadMutationLoading}
            clearingCache={clearCacheMutationLoading}
            onReload={() => void reloadMutation()}
            onClearCache={() => void clearCacheMutation()}
          />
        </Flex>

        {deploymentInProgress && (
          <StatusCallout
            title="Deployment in progress"
            tone="blue"
            message={`There ${
              status.inProgressDeployments.length === 1 ? 'is' : 'are'
            } ${status.inProgressDeployments.length} deployment${
              status.inProgressDeployments.length === 1 ? '' : 's'
            } currently running. The live runtime may not match the newest config until promotion succeeds.`}
            action={
              <Flex gap="8px" wrap="wrap">
                {status.inProgressDeployments.map(deployment => (
                  <Button
                    key={deployment.id}
                    as={Link}
                    to={`/admin/deployments/${deployment.id}`}
                    size="xs"
                    variant="ghost"
                  >
                    View attempt {deployment.attempt}
                  </Button>
                ))}
              </Flex>
            }
          />
        )}

        <SimpleGrid columns={[1, 1, 2, 5]} spacing="12px">
          <ServiceCard service={status.manager} />
          <ServiceCard service={status.subgraphsService} />
          <ServiceCard service={status.cubeService} />
          <ServiceCard service={status.cubeIntrospection} />
          <ServiceCard service={status.cache} />
        </SimpleGrid>

        <SimpleGrid columns={[1, 1, 2]} spacing="16px">
          <LiveRuntimeCard
            liveConfig={status.liveConfig}
            liveDeployment={status.liveDeployment}
            artifacts={status.liveSmrtArtifacts}
          />
          <DeploymentCard
            liveDeployment={status.liveDeployment}
            inProgressDeployments={status.inProgressDeployments}
            pipeline={runtime?.pipeline}
          />
        </SimpleGrid>

        {!status.manager.accessible && (
          <StatusCallout
            title="DB subgraphs manager is not running yet"
            tone="red"
            message={
              status.manager.error ??
              'The API cannot reach the db-subgraphs manager. Runtime details and controls will become available once it starts.'
            }
          />
        )}

        {status.manager.accessible && !runtime?.subgraphsServiceRunning && (
          <StatusCallout
            title="Subgraphs service is not running yet"
            tone="yellow"
            message="The manager is reachable, but the Customer Data API subgraphs app has not started or has not promoted a runtime yet."
          />
        )}

        <Flex direction="column" gap="12px">
          <Flex justify="space-between" align="center">
            <Text variant="sectionHeader">Live Subgraphs</Text>
            <AdminBadge colorScheme={subgraphs.length > 0 ? 'green' : 'gray'}>
              {subgraphs.length} subgraph{subgraphs.length === 1 ? '' : 's'}
            </AdminBadge>
          </Flex>
          {subgraphs.length > 0 ? (
            <Flex direction="column" gap="16px">
              {subgraphs.map(subgraph => (
                <SubgraphRuntimeCard key={subgraph.id} subgraph={subgraph} />
              ))}
            </Flex>
          ) : (
            <Card p="16px">
              <Text fontSize="sm" color="presets.neutral.70">
                No live subgraphs are reporting yet. If a deployment is running,
                this section will populate after the new runtime starts.
              </Text>
            </Card>
          )}
        </Flex>
      </Flex>
    </AdminPageLayout>
  )
}
